export const isObjectEmpty = (obj: Record<string | number, any>): boolean =>
    !Object.keys(obj).length;

/**
 * Compares two objects and returns an object with properties that are either:
 * - Absent in `from` but present in `to`
 * - Different between `from` and `to`
 * - Absent in `to` but present in `from`
 *
 * @param {Record<string, any>} from - The initial object to compare.
 * @param {Record<string, any>} to - The updated object to compare.
 * @returns {Record<string, any>} - An object containing only the properties that differ in `to`
 *     compared to `from`.
 */
export const diff = (from: Record<string, any>, to: Record<string, any>): Record<string, any> => {
    const result: Record<string, any> = {};

    Object.keys({ ...from, ...to }).forEach((key) => {
        if (from[key] !== to[key]) {
            result[key] = to[key];
        }
    });

    return result;
};

export const removeFalsyProperties = (obj: Record<string, any>): Record<string, any> => {
    // Create a new object to store the filtered properties
    const filteredObject: Record<string, any> = {};

    // Use Object.entries to iterate over the key-value pairs
    Object.entries(obj).forEach(([key, value]) => {
        // If the value is truthy, add it to the filtered object
        if (value) {
            filteredObject[key] = value;
        }
    });

    return filteredObject;
};

export const isEmpty = (obj: any): boolean => {
    if (obj === null || obj === undefined) {
        return true;
    }
    if (typeof obj === 'string' || Array.isArray(obj)) {
        return obj.length === 0;
    }
    if (typeof obj === 'object') {
        return Object.keys(obj).length === 0 || Object.values(obj).every((i) => i === undefined);
    }
    return false;
};

export const objectArrayValueToString = (obj?: Record<string, any>): Record<string, any> => {
    const result: Record<string, any> = {};
    if (!obj) {
        return result;
    }
    Object.keys(obj).forEach((key) => {
        if (Array.isArray(obj[key])) {
            result[key] = obj[key].join(',');
        } else {
            result[key] = obj[key];
        }
    });
    return result;
};
